import type { UniqueLabel } from "@lxc/app-device-common";
import dayjs from "dayjs";
import {
  type FilterOptions,
  type FilterSelectionDefinition,
  type FilterSelectionValue,
  OPERATOR_RSQL_NEGATE,
  type Option,
} from "~/core/models/filters";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { Role } from "~/modules/user/models/Role.interface";
import type { User } from "~/modules/user/models/User.interface";
import type { UserGroup } from "~/modules/user/models/UserGroup.interface";
import type { UserSessionSector } from "~/modules/user/models/UserSessionSector.interface";

/**
 * Format a filter value to be used with RSQL requests
 * Replaces all backslashes (\) with double backslashes (\\)
 * Replaces all double quotes (") with escaped double quotes (\")
 * Add double quotes around the final value
 * @param value
 */
function formatRSQLValue(value: FilterSelectionValue): FilterSelectionValue {
  if (Array.isArray(value)) {
    return value.map(formatRSQLValue) as FilterSelectionValue;
  } else {
    let newValue = (value as string).replaceAll("\\", "\\\\");
    newValue = newValue.replaceAll('"', '\\"');
    return `"${newValue}"`;
  }
}

function getTagText(prefix: string, value?: string | null): string | undefined {
  if (value) {
    return `${prefix.toLowerCase()} ${value}`;
  }
}

function getTag(
  prefix: string,
  uid?: string,
  value?: string | null,
  disabled = false,
): UniqueLabel | undefined {
  const label = getTagText(prefix, value);

  if (label && uid) {
    return {
      label,
      uid,
      disabled,
      deletable: true,
    };
  }

  return undefined;
}

function getDtwinTag(
  dtwin: Dtwin,
  deviceLabel: string,
): UniqueLabel | undefined {
  return getTag(
    deviceLabel,
    dtwin.uid,
    dtwin?.attributes?.friendlyName ||
      dtwin.attributes?.serialNumber ||
      dtwin.uid,
  );
}

function getPeriodTag(
  serializedStartDate: string,
  tagDateFormatter: string,
  prefix: string,
  periodSeparator: string,
  serializedEndDate?: string,
): UniqueLabel | undefined {
  let tag: UniqueLabel | undefined;

  if (serializedStartDate) {
    const displayStartDate =
      dayjs(serializedStartDate).format(tagDateFormatter);

    if (prefix) {
      if (serializedEndDate) {
        const displayEndDate =
          dayjs(serializedEndDate).format(tagDateFormatter);
        if (displayStartDate !== displayEndDate) {
          tag = getTag(
            prefix,
            `${serializedStartDate}-${serializedEndDate}`,
            `${displayStartDate} ${periodSeparator} ${displayEndDate}`,
          );
        }
      }

      if (!tag) {
        tag = getTag(prefix, serializedStartDate, displayStartDate);
      }
    }
  }

  return tag;
}

function getRoleTag(role: Role, roleLabel: string): UniqueLabel | undefined {
  return getTag(
    roleLabel,
    String(role.id),
    role.translation?.label ?? role.code,
  );
}

function getSectorTag(
  sector: UserSessionSector,
  sectorLabel: string,
): UniqueLabel | undefined {
  return getTag(sectorLabel, sector.code, sector.name);
}

function getUserTag(user: User, userLabel: string): UniqueLabel | undefined {
  return getTag(userLabel, user.login, user.login);
}

function getUserGroupTag(
  userGroup: UserGroup,
  userGroupLabel: string,
): UniqueLabel | undefined {
  return getTag(
    userGroupLabel,
    userGroup.code,
    userGroup.label ?? userGroup.code,
  );
}

function getDisabledOptionValues(
  value: FilterSelectionValue,
  options?: Option[],
): FilterSelectionValue {
  return (
    options
      ?.filter(
        (option) =>
          option.disabled &&
          Array.isArray(value) &&
          value.includes(option.value as never),
      )
      .map((option) => option.value) ?? []
  );
}

function findDisabledOptionValue(
  value: FilterSelectionValue,
  options?: Option[],
): FilterSelectionValue {
  return (
    options?.find((option) => option.disabled && option.value === value)
      ?.value ?? ""
  );
}

function cloneFilterSelectionDefinition(
  filterSelectionDefinition: FilterSelectionDefinition,
): FilterSelectionDefinition {
  const { key, operator } = filterSelectionDefinition;
  const value = (filterSelectionDefinition.value as string).slice(0);
  const cloneFilterSelectionDefinition: FilterSelectionDefinition = {
    key,
    operator,
    value,
  };

  if (filterSelectionDefinition.fallbackValue != null) {
    cloneFilterSelectionDefinition.fallbackValue = (
      filterSelectionDefinition.fallbackValue as string
    ).slice(0);
  }

  return cloneFilterSelectionDefinition;
}

function buildFilterOptions(
  filterOptions: FilterOptions,
  isDisabled?: boolean,
  disabledOptions?: string[],
): FilterOptions {
  return {
    label: filterOptions.label,
    options: filterOptions.options.map((option) => {
      return {
        label: option.label,
        value: option.value,
        disabled: isDisabled ?? disabledOptions?.includes(option.value),
      };
    }),
  };
}

function negateRSQLExpression(value: FilterSelectionValue): string {
  return `${OPERATOR_RSQL_NEGATE}(${value})`;
}

export default {
  formatRSQLValue,
  buildFilterOptions,
  cloneFilterSelectionDefinition,
  getDtwinTag,
  getDisabledOptionValues,
  findDisabledOptionValue,
  getPeriodTag,
  getRoleTag,
  getSectorTag,
  getUserGroupTag,
  getUserTag,
  getTag,
  getTagText,
  negateRSQLExpression,
};
